import { useEffect, useRef, useState } from 'react';
import './ScrollingLabel.css';

// --- Shared ResizeObserver ---------------------------------------------
// One ResizeObserver instance serves every ScrollingLabel on the page,
// instead of each label creating its own. The browser already batches
// every observed target's notifications into a single callback per
// frame, so a shared instance doesn't cost extra responsiveness — it
// just avoids allocating (and tearing down) a separate native observer
// object per label. That matters here because this component is meant
// to be dropped into list rows (playlist names, track title/artist),
// where a single re-render can mount or unmount dozens of instances at
// once.
const resizeCallbacks = new Map();
let sharedResizeObserver = null;

function getSharedResizeObserver() {
  if (!sharedResizeObserver) {
    sharedResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const callback = resizeCallbacks.get(entry.target);
        if (callback) callback();
      }
    });
  }
  return sharedResizeObserver;
}

// Starts watching `el` for size changes via the shared observer. Also
// covers the *initial* measurement: per spec, ResizeObserver fires its
// callback once for a target as soon as observe() is called, even
// before any actual resize, so there's no need for a separate "check
// once on mount" codepath.
function observeResize(el, callback) {
  resizeCallbacks.set(el, callback);
  getSharedResizeObserver().observe(el);
  return () => {
    resizeCallbacks.delete(el);
    sharedResizeObserver.unobserve(el);
  };
}

// --- Idle scheduling -----------------------------------------------------
// Registering the resize observation (and thus doing the first overflow
// measurement) is deferred to idle time, so mounting a long list of
// labels all at once — e.g. every row of a freshly-loaded playlist —
// doesn't add measurement work to that render's critical path. Falls
// back to a short timeout on browsers without requestIdleCallback
// (Safari).
function scheduleIdle(fn) {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(fn, { timeout: 300 });
    return () => window.cancelIdleCallback(id);
  }
  const id = setTimeout(fn, 100);
  return () => clearTimeout(id);
}

/**
 * Single-line label that auto-scrolls (marquee-style) only when its text
 * doesn't fit its container — otherwise it renders normally (truncated
 * with an ellipsis if needed) and never animates anything. Meant for
 * free-text fields that can easily run longer than the space available
 * for them: playlist names, track title/artist, and similar.
 *
 * Usage: <ScrollingLabel text={someString} className="optional-extra-class" />
 */
export default function ScrollingLabel({ text, className = '' }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return undefined;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setIsOverflowing(false);
      return undefined;
    }

    const checkOverflow = () => {
      // scrollWidth/clientWidth are both rounded to whole pixels, which
      // can silently swallow a fractional overflow -- e.g. a real
      // 59.47px-wide label in a real 59px-wide container reads as
      // scrollWidth 59 vs clientWidth 59, "fits", even though it's
      // genuinely wider by nearly half a pixel. That's a real,
      // reproducible case (confirmed against this exact playlist
      // name), not a hypothetical edge case, so compare the sub-pixel
      // -accurate getBoundingClientRect() widths instead. A tiny
      // (0.1px) tolerance only absorbs true floating-point noise
      // between repeated measurements -- small enough not to mask a
      // real overflow like the one above.
      const measureWidth = measure.getBoundingClientRect().width;
      const containerWidth = container.getBoundingClientRect().width;
      setIsOverflowing(measureWidth > containerWidth + 0.1);
    };

    let stopObserving;
    const cancelIdleSetup = scheduleIdle(() => {
      // Re-check whenever the space available to the label changes
      // (window/panel resize). Every check reads both elements' current
      // getBoundingClientRect() widths directly (see checkOverflow
      // above), so it's always accurate regardless of which element's
      // resize triggered it -- no need to separately watch the measure
      // span itself.
      stopObserving = observeResize(container, checkOverflow);
    });

    return () => {
      cancelIdleSetup();
      if (stopObserving) stopObserving();
    };
  }, [text]);

  return (
    <span className={`scrolling-label ${className}`} ref={containerRef}>
      {/* Invisible single-line copy used only to measure the text's
          natural width against the container — never shown. */}
      <span className="scrolling-label-measure" ref={measureRef}>{text}</span>
      <span className={`scrolling-label-track ${isOverflowing ? 'scrolling' : ''}`}>
        <span className="scrolling-label-text">{text}</span>
        {isOverflowing && (
          <span className="scrolling-label-text" aria-hidden="true">{text}</span>
        )}
      </span>
    </span>
  );
}
